/**
 * The recap's sound effects, synthesised with the Web Audio API: no audio files
 * to ship. Each kind of motion has its own sound, shaped like the motion: a
 * slam rushes in as the type falls and hits when it lands, ticks slow down as
 * a counter settles, a card wipe sweeps in from the side it opens on.
 *
 * Everything plays through one small mix: a little room reverb so sounds sit
 * together, and a limiter so a pile-up never clips. Phone speakers barely play
 * anything under ~200 Hz, so the low thumps are driven into soft distortion to
 * give them harmonics a phone can play.
 *
 * No two plays are quite alike: each one is nudged a little in pitch, level,
 * timing and position, and the busiest sounds have a few versions to pick
 * from, so a run of pops or ticks sounds played, not stamped.
 *
 * Browsers only allow sound after a tap or key press, so nothing plays until
 * `unlockSound()` runs from one. The volume and muting are remembered on this
 * device.
 */

import { useSyncExternalStore } from "react";

export type SoundName =
  | "whoosh" // a card opening: air sweeping past
  | "swish" // something gliding up into place
  | "pop" // something bouncing in, overshoot and all
  | "pluck" // one letter landing (a run of them is a marimba arpeggio)
  | "knock" // one word hopping in (woodblock)
  | "riser" // the rush while big type falls toward the screen
  | "slam" // big type landing: crack, thump, a little room
  | "rumble" // the screen shaking after a slam
  | "tick" // a counter or dial clicking over
  | "ding" // a count arriving at its number (bell)
  | "flip" // a card turning over
  | "sparkle" // a reveal
  | "zip" // a bar growing
  | "chunk" // a slot-machine reel locking
  | "scratch" // a pen striking something out
  | "drip" // one falling drop
  | "right"
  | "wrong"
  | "shutter"; // saving the image

export type SoundOpts = {
  /** Left -1 to right 1. */
  pan?: number;
  /** Seconds, for sounds that last as long as their motion (riser, rumble, zip, flip, whoosh). */
  length?: number;
};

const KEY = "wrapped-sound";
const VOLUME_KEY = "wrapped-volume";
const listeners = new Set<() => void>();
let on = readPreference();
let volume = readVolume();
let audio: AudioContext | null = null;
let mix: Mix | null = null;

function readVolume(): number {
  try {
    const saved = Number(localStorage.getItem(VOLUME_KEY));
    return localStorage.getItem(VOLUME_KEY) !== null && Number.isFinite(saved) ? Math.max(0, Math.min(1, saved)) : 0.8;
  } catch {
    return 0.8;
  }
}

function readPreference(): boolean {
  try {
    return localStorage.getItem(KEY) !== "off";
  } catch {
    return true;
  }
}

export const soundOn = () => on;

export function setSoundOn(value: boolean) {
  on = value;
  try {
    localStorage.setItem(KEY, value ? "on" : "off");
  } catch {
    /* private mode: the choice lasts this visit only */
  }
  // Muting silences the song too, and stops it rather than playing it to nobody.
  if (value && musicWanted) playMusic();
  else applyMusicLevel();
  listeners.forEach((listener) => listener());
}

export const getVolume = () => volume;

/** 0 to 1. Takes effect at once, on anything already playing too. */
export function setVolume(value: number) {
  volume = Math.max(0, Math.min(1, value));
  try {
    localStorage.setItem(VOLUME_KEY, String(volume));
  } catch {
    /* private mode: the choice lasts this visit only */
  }
  if (mix) mix.volume.gain.setTargetAtTime(volume, mix.ctx.currentTime, 0.02);
  listeners.forEach((listener) => listener());
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** `[on, toggle]` for a mute button. */
export function useSound(): [boolean, () => void] {
  const value = useSyncExternalStore(subscribe, soundOn, soundOn);
  return [value, () => setSoundOn(!soundOn())];
}

/** `[volume, setVolume]` for a slider. */
export function useVolume(): [number, (value: number) => void] {
  return [useSyncExternalStore(subscribe, getVolume, getVolume), setVolume];
}

// ---------- the song ----------
//
// An optional song an admin uploaded, looping quietly under the effects. It is
// streamed (an <audio> element) rather than decoded whole, so a long song costs
// a phone little memory, and routed through the same mix so the volume slider
// and mute cover it: an iPhone ignores an <audio> element's own volume.

const MUSIC_KEY = "wrapped-music";
/** A bed under the effects, not over them. */
const MUSIC_LEVEL = 0.45;
let musicOn = readMusicPreference();
let musicUrl: string | null = null;
let musicEl: HTMLAudioElement | null = null;
let musicGain: GainNode | null = null;
/** The story is playing and wants its song. */
let musicWanted = false;

function readMusicPreference(): boolean {
  try {
    return localStorage.getItem(MUSIC_KEY) !== "off";
  } catch {
    return true;
  }
}

export const musicIsOn = () => musicOn;

export function setMusicOn(value: boolean) {
  musicOn = value;
  try {
    localStorage.setItem(MUSIC_KEY, value ? "on" : "off");
  } catch {
    /* private mode: the choice lasts this visit only */
  }
  if (value && musicWanted) playMusic();
  else applyMusicLevel();
  listeners.forEach((listener) => listener());
}

/** `[on, toggle]` for the song's switch. */
export function useMusicOn(): [boolean, () => void] {
  const value = useSyncExternalStore(subscribe, musicIsOn, musicIsOn);
  return [value, () => setMusicOn(!musicIsOn())];
}

/** Fade the song toward where it should be: up while wanted and allowed, down otherwise. */
function applyMusicLevel() {
  const audible = on && musicOn && musicWanted;
  if (musicGain && audio) musicGain.gain.setTargetAtTime(audible ? MUSIC_LEVEL : 0, audio.currentTime, audible ? 0.5 : 0.12);
  if (!audible && musicEl) {
    const el = musicEl;
    // Pause once it has faded, so a muted song isn't streaming to nobody.
    setTimeout(() => {
      if (!(on && musicOn && musicWanted)) el.pause();
    }, 400);
  }
}

/** Which song the story has; null for none. */
export function setMusic(url: string | null) {
  if (url === musicUrl) return;
  stopMusic();
  musicUrl = url;
  musicEl = null;
  musicGain = null;
}

/** Start or carry on with the song. Call from a tap or key press the first time: browsers insist. */
export function playMusic() {
  musicWanted = true;
  if (!musicUrl || !audio || !on || !musicOn) return applyMusicLevel();
  try {
    mix ??= mixFor(audio, volume);
    if (!musicEl) {
      musicEl = new Audio(musicUrl);
      musicEl.loop = true;
      musicEl.preload = "auto";
      try {
        musicGain = audio.createGain();
        musicGain.gain.value = 0;
        audio.createMediaElementSource(musicEl).connect(musicGain).connect(mix.volume);
      } catch {
        // No routing through the mix here: the element plays on its own, at its own level.
        musicGain = null;
        musicEl.volume = MUSIC_LEVEL * volume;
      }
    }
    applyMusicLevel();
    void musicEl.play()?.catch(() => {});
  } catch {
    /* a song is never worth breaking the story over */
  }
}

/** The story paused: the song fades out and waits. */
export function pauseMusic() {
  musicWanted = false;
  applyMusicLevel();
}

/** The story closed: back to the start. */
export function stopMusic() {
  musicWanted = false;
  if (musicEl) {
    musicEl.pause();
    musicEl.currentTime = 0;
  }
}

/** Call from a tap or key press: the first one is what lets audio start. */
export function unlockSound() {
  // Older iPhones only have the prefixed one.
  const scope = globalThis as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext };
  const Context = scope.AudioContext ?? scope.webkitAudioContext;
  if (!Context) return;
  audio ??= new Context();
  if (audio.state === "suspended") void audio.resume();
}

// ---------- the mix ----------

type Mix = {
  ctx: BaseAudioContext;
  dry: AudioNode;
  wet: AudioNode;
  /** The listener's volume, after the limiter so turning it down never changes the sound. */
  volume: GainNode;
  noise: AudioBuffer;
  drive: Float32Array<ArrayBuffer>;
};

/** A second of white noise, shared by every noisy sound (each reads from a random spot). */
function noiseBuffer(ctx: BaseAudioContext): AudioBuffer {
  const buffer = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  return buffer;
}

/** A small room: noise that dies away over about half a second. */
function roomImpulse(ctx: BaseAudioContext, seconds: number): AudioBuffer {
  const length = Math.ceil(ctx.sampleRate * seconds);
  const buffer = ctx.createBuffer(2, length, ctx.sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const data = buffer.getChannelData(ch);
    for (let i = 0; i < length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / length) ** 3;
  }
  return buffer;
}

/** Soft clipping: a sine thump comes out with the overtones a small speaker can play. */
function driveCurve(amount: number): Float32Array<ArrayBuffer> {
  const curve = new Float32Array(new ArrayBuffer(1024 * 4));
  for (let i = 0; i < 1024; i++) {
    const x = (i / 1023) * 2 - 1;
    curve[i] = Math.tanh(amount * x) / Math.tanh(amount);
  }
  return curve;
}

/** The shared mix for a context: dry and reverb sends into a limiter, then the volume. */
export function mixFor(ctx: BaseAudioContext, level = 1): Mix {
  const master = ctx.createGain();
  master.gain.value = 1.4;
  const limiter = ctx.createDynamicsCompressor();
  limiter.threshold.value = -8;
  limiter.knee.value = 6;
  limiter.ratio.value = 12;
  limiter.attack.value = 0.002;
  limiter.release.value = 0.12;
  const listen = ctx.createGain();
  listen.gain.value = level;
  master.connect(limiter).connect(listen).connect(ctx.destination);
  const room = ctx.createConvolver();
  room.buffer = roomImpulse(ctx, 0.7);
  const wet = ctx.createGain();
  wet.gain.value = 0.5;
  wet.connect(room).connect(master);
  return { ctx, dry: master, wet, volume: listen, noise: noiseBuffer(ctx), drive: driveCurve(3) };
}

// ---------- building blocks ----------

/** Where one sound plays: a panned channel into the mix, `room` of it into the reverb. */
type Voice = { ctx: BaseAudioContext; out: AudioNode; t: number; mix: Mix; panner: StereoPannerNode | null };

/** `x` give or take a fraction of itself, at random. */
const jitter = (x: number, amount: number) => x * (1 + (Math.random() * 2 - 1) * amount);
/** One of these, at random. */
const pick = <T,>(...options: T[]): T => options[Math.floor(Math.random() * options.length)];

function voice(m: Mix, pan: number, room: number): Voice {
  const { ctx } = m;
  const input = ctx.createGain();
  // No two plays alike: a touch louder or softer, a hair late, a little to one side.
  input.gain.value = jitter(1, 0.15);
  const panner = typeof ctx.createStereoPanner === "function" ? ctx.createStereoPanner() : null;
  if (panner) panner.pan.value = Math.max(-1, Math.min(1, pan + (Math.random() * 2 - 1) * 0.12));
  const send = ctx.createGain();
  send.gain.value = room;
  const head = panner ?? input;
  if (panner) input.connect(panner);
  head.connect(m.dry);
  head.connect(send);
  send.connect(m.wet);
  return { ctx, out: input, t: ctx.currentTime + 0.005 + Math.random() * 0.008, mix: m, panner };
}

const FLOOR = 0.0001;

/** A percussive envelope: up in `attack`, down to silence over `decay`. */
function envelope(param: AudioParam, t: number, attack: number, peak: number, decay: number) {
  param.setValueAtTime(FLOOR, t);
  param.exponentialRampToValueAtTime(Math.max(peak, FLOOR * 2), t + Math.max(attack, 0.001));
  param.exponentialRampToValueAtTime(FLOOR, t + Math.max(attack, 0.001) + decay);
}

type ToneOpts = {
  type?: OscillatorType;
  freq: number;
  /** Glide to this frequency over `glide` seconds. */
  to?: number;
  glide?: number;
  at?: number;
  attack?: number;
  peak: number;
  decay: number;
  driven?: boolean;
};

function tone(v: Voice, { type = "sine", freq, to, glide, at = 0, attack = 0.003, peak, decay, driven = false }: ToneOpts) {
  const t = v.t + at;
  const osc = v.ctx.createOscillator();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t);
  if (to) osc.frequency.exponentialRampToValueAtTime(to, t + (glide ?? decay));
  const gain = v.ctx.createGain();
  envelope(gain.gain, t, attack, peak, decay);
  let head: AudioNode = osc;
  if (driven) {
    const shaper = v.ctx.createWaveShaper();
    shaper.curve = v.mix.drive;
    head = osc.connect(shaper);
  }
  head.connect(gain).connect(v.out);
  osc.start(t);
  osc.stop(t + attack + decay + 0.05);
}

type NoiseOpts = {
  filter: BiquadFilterType;
  freq: number;
  to?: number;
  q?: number;
  at?: number;
  attack?: number;
  peak: number;
  decay: number;
};

function hiss(v: Voice, { filter, freq, to, q = 1, at = 0, attack = 0.001, peak, decay }: NoiseOpts) {
  const t = v.t + at;
  const source = v.ctx.createBufferSource();
  source.buffer = v.mix.noise;
  const band = v.ctx.createBiquadFilter();
  band.type = filter;
  band.Q.value = q;
  band.frequency.setValueAtTime(freq, t);
  if (to) band.frequency.exponentialRampToValueAtTime(to, t + attack + decay);
  const gain = v.ctx.createGain();
  envelope(gain.gain, t, attack, peak, decay);
  source.connect(band).connect(gain).connect(v.out);
  source.start(t, Math.random() * 1.5);
  source.stop(t + attack + decay + 0.05);
}

/** A swelling noise sweep, loudest at `peakAt` (a fraction of its length): the shape of a fast pass. */
function sweep(v: Voice, opts: { freq: number; mid: number; to: number; length: number; peakAt: number; peak: number; q?: number }) {
  const { length, peakAt, peak } = opts;
  const t = v.t;
  const source = v.ctx.createBufferSource();
  source.buffer = v.mix.noise;
  const band = v.ctx.createBiquadFilter();
  band.type = "bandpass";
  band.Q.value = opts.q ?? 0.9;
  band.frequency.setValueAtTime(opts.freq, t);
  band.frequency.exponentialRampToValueAtTime(opts.mid, t + length * peakAt);
  band.frequency.exponentialRampToValueAtTime(opts.to, t + length);
  const gain = v.ctx.createGain();
  gain.gain.setValueAtTime(FLOOR, t);
  gain.gain.exponentialRampToValueAtTime(peak, t + length * peakAt);
  gain.gain.exponentialRampToValueAtTime(FLOOR, t + length);
  source.connect(band).connect(gain).connect(v.out);
  source.start(t, Math.random() * 1.5);
  source.stop(t + length + 0.05);
}

/** A run of notes up a major pentatonic: `step` 0 is the root, 5 the octave above. */
export function scaleStep(step: number): number {
  const PENTATONIC = [0, 2, 4, 7, 9];
  const octave = Math.floor(step / 5);
  return 2 ** ((PENTATONIC[((step % 5) + 5) % 5] + 12 * octave) / 12);
}

// ---------- the sounds ----------

type Maker = (m: Mix, pitch: number, opts: SoundOpts) => void;

const SOUNDS: Record<SoundName, Maker> = {
  whoosh(m, pitch, { pan = 0, length = 0.6 }) {
    const v = voice(m, pan, 0.35);
    // The wipe eases in and out, fastest in the middle: so is the air. Each pass a different gust.
    sweep(v, {
      freq: jitter(260, 0.2) * pitch,
      mid: jitter(1900, 0.2) * pitch,
      to: jitter(700, 0.25) * pitch,
      length: jitter(length, 0.08),
      peakAt: jitter(0.45, 0.12),
      peak: 0.26,
      q: jitter(0.9, 0.3),
    });
    // It drifts from the side the circle opens on toward the middle.
    v.panner?.pan.setValueAtTime(pan, v.t);
    v.panner?.pan.linearRampToValueAtTime(pan * 0.2, v.t + length);
  },

  swish(m, pitch, { pan = 0 }) {
    const v = voice(m, pan, 0.2);
    sweep(v, { freq: jitter(1500, 0.2) * pitch, mid: jitter(4200, 0.2) * pitch, to: 3000 * pitch, length: jitter(0.22, 0.15), peakAt: 0.3, peak: 0.12, q: 1.4 });
  },

  pop(m, pitch, { pan = 0 }) {
    const v = voice(m, pan, 0.15);
    // Up past the note and back, like the scale's overshoot. Some bubbles are rounder,
    // some brighter, some bounce further.
    const lift = pick(1.55, 1.7, 1.85);
    const colour = pick<[OscillatorType, number]>(["sine", 2], ["sine", 1.5], ["triangle", 2]);
    tone(v, { type: colour[0], freq: 520 * pitch, to: 520 * lift * pitch, glide: jitter(0.045, 0.25), peak: 0.24, decay: jitter(0.12, 0.2) });
    tone(v, { freq: 520 * colour[1] * pitch, to: 520 * colour[1] * lift * pitch, glide: 0.045, peak: 0.05, decay: 0.07 });
    hiss(v, { filter: "highpass", freq: jitter(3000, 0.3), peak: 0.05, decay: 0.008 });
  },

  pluck(m, pitch, { pan = 0 }) {
    const v = voice(m, pan, 0.25);
    // A marimba bar (the note and a quick fourth partial for the mallet), or now and
    // then a kalimba tine, which rings with different overtones.
    const kalimba = Math.random() < 0.35;
    tone(v, { freq: 660 * pitch, peak: 0.14, decay: jitter(kalimba ? 0.32 : 0.22, 0.2), attack: 0.002 });
    if (kalimba) tone(v, { freq: 660 * 5.4 * pitch, peak: 0.025, decay: 0.08, attack: 0.001 });
    else tone(v, { freq: 660 * 3.99 * pitch, peak: 0.04, decay: 0.04, attack: 0.001 });
  },

  knock(m, pitch, { pan = 0 }) {
    const v = voice(m, pan, 0.2);
    // Three woodblocks, like a percussionist's set, and never quite the same strike.
    const block = pick(1, 1.19, 0.84);
    hiss(v, { filter: "bandpass", freq: jitter(1300 * block, 0.05) * pitch, q: jitter(5, 0.3), peak: 0.6, decay: jitter(0.05, 0.2) });
    tone(v, { freq: 820 * block * pitch, peak: 0.15, decay: 0.05, attack: 0.001 });
  },

  riser(m, pitch, { pan = 0, length = 0.3 }) {
    const v = voice(m, pan, 0.1);
    const t = v.t;
    // The fall speeds up (power4.in), so the rush grows the same way and stops dead at the hit.
    const curve = new Float32Array(32).map((_, i) => Math.max(FLOOR, 0.22 * (i / 31) ** 4));
    const source = v.ctx.createBufferSource();
    source.buffer = v.mix.noise;
    const band = v.ctx.createBiquadFilter();
    band.type = "bandpass";
    band.Q.value = 0.8;
    band.frequency.setValueAtTime(300 * pitch, t);
    band.frequency.exponentialRampToValueAtTime(2600 * pitch, t + length);
    const gain = v.ctx.createGain();
    gain.gain.setValueCurveAtTime(curve, t, length);
    source.connect(band).connect(gain).connect(v.out);
    source.start(t, Math.random() * 1.5);
    source.stop(t + length);
  },

  slam(m, pitch, { pan = 0 }) {
    const v = voice(m, pan, 0.4);
    // The crack: a hard transient, the "slap" itself, sharper or duller each time.
    hiss(v, { filter: "highpass", freq: jitter(1500, 0.25), peak: 0.75, decay: jitter(0.06, 0.25) });
    hiss(v, { filter: "bandpass", freq: jitter(1200, 0.2) * pitch, q: 0.9, peak: 0.5, decay: 0.1 });
    // The body of the hit.
    hiss(v, { filter: "bandpass", freq: jitter(520, 0.15) * pitch, q: 1.1, peak: 0.45, decay: jitter(0.14, 0.2) });
    // The thwack: a driven mid knock, the part of the hit a phone speaker plays.
    tone(v, { freq: jitter(420, 0.1) * pitch, to: 160 * pitch, glide: 0.06, peak: 0.6, decay: 0.12, attack: 0.001, driven: true });
    // The thump: a falling kick, driven too.
    tone(v, { freq: 220 * pitch, to: 55 * pitch, glide: 0.1, peak: 0.45, decay: 0.18, attack: 0.001, driven: true });
    // And the floor under it, for headphones.
    tone(v, { freq: 52 * pitch, peak: 0.14, decay: 0.35, attack: 0.004 });
  },

  rumble(m, pitch, { pan = 0, length = 0.3 }) {
    const v = voice(m, pan, 0.2);
    const t = v.t;
    // The shake jumps every 30 ms and dies down; the rumble shudders at that rate and dies with it.
    const source = v.ctx.createBufferSource();
    source.buffer = v.mix.noise;
    const low = v.ctx.createBiquadFilter();
    low.type = "lowpass";
    low.frequency.value = 900 * pitch;
    const shudder = v.ctx.createGain();
    const lfo = v.ctx.createOscillator();
    lfo.frequency.value = 33;
    const depth = v.ctx.createGain();
    depth.gain.value = 0.5;
    shudder.gain.value = 0.5;
    lfo.connect(depth).connect(shudder.gain);
    const gain = v.ctx.createGain();
    gain.gain.setValueAtTime(0.6, t);
    gain.gain.exponentialRampToValueAtTime(FLOOR, t + length);
    source.connect(low).connect(shudder).connect(gain).connect(v.out);
    source.start(t, Math.random() * 1.5);
    lfo.start(t);
    source.stop(t + length + 0.05);
    lfo.stop(t + length + 0.05);
  },

  tick(m, pitch, { pan = 0 }) {
    const v = voice(m, pan, 0.05);
    // A mechanical click: a snap of noise and a tiny pitched ping, the teeth of a gear
    // not all alike.
    hiss(v, { filter: "highpass", freq: jitter(3500, 0.2), peak: jitter(0.34, 0.2), decay: 0.012 });
    tone(v, { freq: jitter(2100, 0.06) * pitch, peak: 0.06, decay: 0.016, attack: 0.001 });
  },

  ding(m, pitch, { pan = 0 }) {
    const v = voice(m, pan, 0.45);
    // A small bell: inharmonic partials, the higher ones dying first.
    [
      [1, 0.17, 1.5],
      [2.76, 0.06, 0.7],
      [5.4, 0.03, 0.35],
    ].forEach(([ratio, peak, decay]) => tone(v, { freq: 1046 * ratio * pitch, peak, decay, attack: 0.002 }));
  },

  flip(m, pitch, { pan = 0, length = 0.8 }) {
    const v = voice(m, pan, 0.25);
    // The turn is fastest edge-on, halfway through: that's where the air is loudest.
    sweep(v, { freq: 700 * pitch, mid: 3800 * pitch, to: 1400 * pitch, length, peakAt: 0.5, peak: 0.22, q: 1.2 });
    hiss(v, { filter: "bandpass", freq: 900 * pitch, q: 2, at: length * 0.95, peak: 0.18, decay: 0.04 });
  },

  sparkle(m, pitch, { pan = 0 }) {
    const v = voice(m, pan, 0.55);
    [0, 1, 2, 3, 4].forEach((step, i) =>
      tone(v, { freq: 1568 * scaleStep(step) * pitch, at: i * 0.045, peak: 0.05, decay: 0.3, attack: 0.002 }),
    );
  },

  zip(m, pitch, { pan = 0, length = 0.6 }) {
    const v = voice(m, pan, 0.15);
    const t = v.t;
    // The bars ease out (fast, then settling), so the pitch shoots up and levels off.
    const osc = v.ctx.createOscillator();
    osc.type = "sawtooth";
    osc.frequency.setValueAtTime(160 * pitch, t);
    osc.frequency.setTargetAtTime(720 * pitch, t, length / 4);
    const low = v.ctx.createBiquadFilter();
    low.type = "lowpass";
    low.frequency.setValueAtTime(900, t);
    low.frequency.setTargetAtTime(3200, t, length / 4);
    const gain = v.ctx.createGain();
    gain.gain.setValueAtTime(FLOOR, t);
    gain.gain.exponentialRampToValueAtTime(0.08, t + 0.02);
    gain.gain.exponentialRampToValueAtTime(FLOOR, t + length);
    osc.connect(low).connect(gain).connect(v.out);
    osc.start(t);
    osc.stop(t + length + 0.05);
  },

  chunk(m, pitch, { pan = 0 }) {
    const v = voice(m, pan, 0.2);
    hiss(v, { filter: "bandpass", freq: 650 * pitch, q: 4, peak: 0.5, decay: 0.07 });
    tone(v, { freq: 230 * pitch, to: 150 * pitch, peak: 0.4, decay: 0.1, attack: 0.001, driven: true });
    hiss(v, { filter: "highpass", freq: 4000, peak: 0.08, decay: 0.01 });
  },

  scratch(m, pitch, { pan = 0 }) {
    const v = voice(m, pan, 0.1);
    // A pen stroke: a few rough bursts in quick succession.
    [0, 0.035, 0.075, 0.12].forEach((at, i) =>
      hiss(v, { filter: "bandpass", freq: (2600 + i * 300) * pitch, q: 3, at, peak: 0.3, decay: 0.04 }),
    );
  },

  drip(m, pitch, { pan = 0 }) {
    const v = voice(m, pan, 0.3);
    // A drop rises in pitch as it lands.
    tone(v, { freq: jitter(900, 0.25) * pitch, to: jitter(1800, 0.25) * pitch, glide: jitter(0.04, 0.3), peak: 0.16, decay: 0.08, attack: 0.001 });
  },

  right(m, pitch, { pan = 0 }) {
    const v = voice(m, pan, 0.4);
    [0, 2, 4, 5].forEach((step, i) => {
      tone(v, { freq: 660 * scaleStep(step) * pitch, at: i * 0.07, peak: 0.13, decay: 0.3, attack: 0.002 });
      tone(v, { freq: 660 * 3.99 * scaleStep(step) * pitch, at: i * 0.07, peak: 0.03, decay: 0.04, attack: 0.001 });
    });
    SOUNDS.sparkle(m, pitch * 1.5, { pan });
  },

  wrong(m, pitch, { pan = 0 }) {
    const v = voice(m, pan, 0.2);
    // "Uh-oh": two notes falling, a bit rough.
    tone(v, { freq: 330 * pitch, to: 300 * pitch, peak: 0.22, decay: 0.16, driven: true });
    tone(v, { freq: 250 * pitch, to: 170 * pitch, at: 0.16, peak: 0.25, decay: 0.3, driven: true });
  },

  shutter(m, pitch, { pan = 0 }) {
    const v = voice(m, pan, 0.15);
    hiss(v, { filter: "highpass", freq: 2500, peak: 0.3, decay: 0.02 });
    tone(v, { freq: 160 * pitch, peak: 0.25, decay: 0.06, attack: 0.001, driven: true });
    hiss(v, { filter: "highpass", freq: 3000, at: 0.07, peak: 0.22, decay: 0.025 });
  },
};

/** Build one sound into a given context's mix (the live one, or an offline one to measure it). */
export function renderSound(m: Mix, name: SoundName, pitch = 1, opts: SoundOpts = {}) {
  // A slightly different note every play, about a quarter-tone either way at most.
  SOUNDS[name](m, jitter(pitch, 0.025), opts);
}

/** Play a sound if unmuted and unlocked; `pitch` nudges it so repeats don't drone. */
export function play(name: SoundName, pitch = 1, opts: SoundOpts = {}) {
  if (!on || !audio) return;
  try {
    mix ??= mixFor(audio, volume);
    renderSound(mix, name, pitch, opts);
  } catch {
    /* a sound effect is never worth breaking the story over */
  }
}
